import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "react-feather";
import { rawColors } from "../../../design-system/tokens/colors";
import type { MultiSelectOption } from "../../../hooks/useMultiSelectSearch";
import type { PortalMenuRect } from "../../../hooks/usePortalMenu";
import {
  Wrap,
  Trigger,
  TriggerValue,
  TriggerChipRow,
  Chip,
  ChipRemove,
  Menu,
  SearchRow,
  SearchInput,
  List,
  Item,
  EmptyState,
  Checkbox,
} from "./MultiSelectSearch.styles";

export interface MultiSelectSearchProps<T extends MultiSelectOption = MultiSelectOption> {
  /** Currently selected option values. */
  value: string[];
  /** All available options. */
  options: T[];
  /** Options filtered by the current search query (passed from useMultiSelectSearch). */
  filtered: T[];
  /** Whether the dropdown menu is open. */
  open: boolean;
  /** Current search query text. */
  query: string;
  /** Toggle the dropdown menu open/closed. */
  onToggleOpen: () => void;
  /** Update the search query. */
  onQueryChange: (q: string) => void;
  /** Toggle an option in the selection. */
  onToggleOption: (optionValue: string) => void;
  /** Ref for the trigger (for anchor positioning). */
  triggerRef: React.RefObject<HTMLButtonElement>;
  /** Ref for the menu (for outside-click detection). */
  menuRef: React.RefObject<HTMLDivElement>;
  /** Computed menu rect from usePortalMenu. */
  menuRect: PortalMenuRect | null;
  placeholder?: string;
  searchPlaceholder?: string;
  /** At the end of the search row (e.g. a mic). */
  searchEndSlot?: React.ReactNode;
  /** Replaces the built-in trigger (e.g. a chip). The caller owns `triggerRef` and `onToggleOpen` on it. */
  trigger?: React.ReactNode;
}

function MultiSelectSearch<T extends MultiSelectOption = MultiSelectOption>({
  value,
  options,
  filtered,
  open,
  query,
  onToggleOpen,
  onQueryChange,
  onToggleOption,
  triggerRef,
  menuRef,
  menuRect,
  placeholder = "Select",
  searchPlaceholder = "Search",
  searchEndSlot,
  trigger,
}: MultiSelectSearchProps<T>) {
  const findOption = (optionValue: string) =>
    options.find((o) => o.value === optionValue);
  const optionLabel = (optionValue: string) =>
    findOption(optionValue)?.label ?? optionValue;
  const optionIcon = (optionValue: string) =>
    findOption(optionValue)?.icon ?? null;

  return (
    <Wrap>
      {trigger !== undefined ? trigger : (
      <Trigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        $open={open}
        onClick={onToggleOpen}
      >
        {value.length === 0 ? (
          <TriggerValue $placeholder>{placeholder}</TriggerValue>
        ) : (
          <TriggerChipRow>
            {value.map((v) => (
              <Chip key={v}>
                {optionIcon(v)}
                {optionLabel(v)}
                <ChipRemove
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${optionLabel(v)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleOption(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleOption(v);
                    }
                  }}
                >
                  <X size={12} />
                </ChipRemove>
              </Chip>
            ))}
          </TriggerChipRow>
        )}
        <ChevronDown size={16} color={rawColors.text.mediumEmphasis} />
      </Trigger>
      )}

      {open &&
        menuRect &&
        createPortal(
          <Menu
            ref={menuRef}
            style={{
              ...(menuRect.bottom != null
                ? { bottom: menuRect.bottom, top: "auto" }
                : { top: menuRect.top, bottom: "auto" }),
              left: menuRect.left,
              width: menuRect.width,
              maxHeight: menuRect.maxHeight,
            }}
            role="listbox"
          >
            <SearchRow>
              <Search size={14} color={rawColors.text.mediumEmphasis} />
              <SearchInput
                autoFocus
                value={query}
                placeholder={searchPlaceholder}
                onChange={(e) => onQueryChange(e.target.value)}
              />
              {searchEndSlot}
            </SearchRow>
            <List>
              {filtered.length === 0 ? (
                <EmptyState>No results</EmptyState>
              ) : (
                filtered.map((option) => {
                  const checked = value.includes(option.value);
                  return (
                    <Item
                      key={option.value}
                      role="option"
                      aria-selected={checked}
                      onClick={() => onToggleOption(option.value)}
                    >
                      <Checkbox $checked={checked} />
                      {option.icon}
                      <span>{option.label}</span>
                    </Item>
                  );
                })
              )}
            </List>
          </Menu>,
          document.body,
        )}
    </Wrap>
  );
}

export default MultiSelectSearch;
